// src/screens/BlogDetailPage.js
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./BlogDetailPage.css";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Logo from "../../img/logo.png";
import bgImage from "../../img/blog_background.png";
import fallbackHero from "../../img/blog.png";
import relatedImage1 from "../../img/blog1.png";
import relatedImage2 from "../../img/blog1.png";
import relatedImage3 from "../../img/blog1.png";
import axios from "axios";
import API_BASE from "../../config/api";

function BlogDetailPage() {
  const { id } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const { user, logout } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ✅ Fetch single blog post by ID
  useEffect(() => {
    const fetchPost = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await axios.get(`${API_BASE}/blog/${id}`, {
          headers: { accept: "application/json" },
        });
        setPost(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError("Blog post not found.");
        } else {
          setError("Failed to load blog post. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchPost();
  }, [id]);

  // ✅ Format ISO date
  const formatDate = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // ✅ Estimate read time
  const estimateReadTime = (content = "") => {
    const words = content.trim().split(/\s+/).length;
    return `${Math.max(1, Math.round(words / 200))} min read`;
  };

  const handleLogout = () => logout();

  const handleClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // ✅ Share functions
  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || "Revexia Blog";
    switch (platform) {
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, "_blank");
        break;
      case "linkedin":
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, "_blank");
        break;
      case "copy":
        navigator.clipboard.writeText(url);
        alert("Link copied to clipboard!");
        break;
      default:
        break;
    }
  };

  // Related articles (static placeholder)
  const relatedArticles = [
    { id: 1, title: "Understanding Buyer Intent Signals", date: "Oct 22", readTime: "5 min read", image: relatedImage1 },
    { id: 2, title: "Guide to Conversational Commerce", date: "Oct 10", readTime: "15 min read", image: relatedImage2 },
    { id: 3, title: "Data Privacy for Operators", date: "Oct 05", readTime: "7 min read", image: relatedImage3 },
  ];

  return (
    <>
      <Helmet>
        <title>{post ? `${post.title} - Revexia` : "Blog - Revexia"}</title>
        <meta name="description" content={post?.excerpt || "Read this article on Revexia."} />
      </Helmet>

      <div className="blog-detail-page">

        {/* ── Navbar ── */}
        <nav className="navbar-custom">
          <div className="navbar-container">
            <div className="navbar-brand-custom">
              <Link to="/"><img src={Logo} alt="Revexia Logo" className="navbar-logo-img" /></Link>
            </div>
            <div className="navbar-center-pill">
              <ul className="nav-menu-pills">
                <li className="nav-item-pill"><Link to="/" className="nav-link-pill">Home</Link></li>
                <li className="nav-item-pill">
                  <a href="/#features" className="nav-link-pill" onClick={handleClick}>Feature</a>
                </li>
                <li className="nav-item-pill nav-item-dropdown">
                  <button className="nav-link-pill nav-dropdown-toggle">
                    Resources
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="dropdown-arrow">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <div className="nav-dropdown-menu">
                    <Link to="/how" className="nav-dropdown-item">How it works</Link>
                    <Link to="/support" className="nav-dropdown-item">Support overview</Link>
                    <Link to="/trust" className="nav-dropdown-item">Trust Center</Link>
                    <Link to="/privacy-policy" className="nav-dropdown-item">Privacy Policy</Link>
                    <Link to="/terms" className="nav-dropdown-item">Terms of Service</Link>
                     <Link to="/facebook-terms" className="nav-dropdown-item">
                                          Facebook Terms
                                        </Link>
                  </div>
                </li>
                <li className="nav-item-pill"><Link to="/blog" className="nav-link-pill">Blog</Link></li>
              </ul>
            </div>
            <div className="navbar-auth-section">
              {user ? (
                <>
                  <Link to="/dashboard" className="btn-nav-outline">Dashboard</Link>
                  <button onClick={handleLogout} className="btn-nav-gradient">Logout</button>
                </>
              ) : (
                <Link to="/auth" className="btn-nav-gradient">Login</Link>
              )}
            </div>
            <button className="navbar-mobile-toggle" type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <span className="navbar-toggler-icon-custom"></span>
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="navbar-mobile-menu">
              <ul className="mobile-nav-list">
                <li><Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link></li>
                <li><a href="/#features" onClick={(e) => { setMobileMenuOpen(false); handleClick(e); }}>Feature</a></li>
                <li><Link to="/how" onClick={() => setMobileMenuOpen(false)}>How it works</Link></li>
                <li><Link to="/support" onClick={() => setMobileMenuOpen(false)}>Support overview</Link></li>
                <li><Link to="/trust" onClick={() => setMobileMenuOpen(false)}>Trust Center</Link></li>
                <li><Link to="/blog" onClick={() => setMobileMenuOpen(false)}>Blog</Link></li>
              </ul>
              <div className="mobile-auth-buttons">
                {user ? (
                  <>
                    <Link to="/dashboard" className="btn-nav-outline" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                    <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="btn-nav-gradient">Logout</button>
                  </>
                ) : (
                  <Link to="/auth" className="btn-nav-gradient" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                )}
              </div>
            </div>
          )}
        </nav>

        {/* ── Loading State ── */}
        {isLoading && (
          <div className="bdp-loading-wrapper">
            <div className="bdp-spinner" />
            <p className="bdp-loading-text">Loading article...</p>
          </div>
        )}

        {/* ── Error State ── */}
        {!isLoading && error && (
          <div className="bdp-error-wrapper">
            <p className="bdp-error-text">⚠ {error}</p>
            <Link to="/blog" className="bdp-back-btn">← Back to Blog</Link>
          </div>
        )}

        {/* ── Main Content ── */}
        {!isLoading && !error && post && (
          <main className="blog-detail-main">

            {/* Article Header */}
            <div className="article-header-wrapper">
              <div className="article-header-bg-effects">
                <div className="article-header-bg-image-wrapper">
                  <img src={bgImage} alt="" className="article-header-bg-image" />
                </div>
                <div className="article-header-gradient-overlay"></div>
              </div>

              <div className="article-header-section">
                {/* Breadcrumb */}
                <div className="breadcrumb-container">
                  <Link to="/blog" className="breadcrumb-link">Blog</Link>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M4.5 9L7.5 6L4.5 3" stroke="#9AA0A6" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="breadcrumb-link">Article</span>
                </div>

                {/* ✅ Dynamic Title */}
                <h1 className="article-title">{post.title}</h1>

                {/* ✅ Dynamic Meta */}
                <div className="article-meta-container">
                  <span className="meta-date">{formatDate(post.created_at)}</span>
                  <span className="meta-separator">•</span>
                  <span className="meta-read-time">{estimateReadTime(post.content)}</span>
                  {post.updated_at !== post.created_at && (
                    <>
                      <span className="meta-separator">•</span>
                      <span className="meta-updated">Updated {formatDate(post.updated_at)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ✅ Hero Image */}
            <div className="hero-image-container">
              <img
                src={post.media_url || fallbackHero}
                alt={post.title}
                className="hero-image"
                onError={(e) => (e.target.src = fallbackHero)}
              />
            </div>

            {/* Content Grid */}
            <div className="content-grid">

              {/* ✅ Article Content */}
              <article className="article-content">

                {/* Excerpt as intro */}
                {post.excerpt && post.excerpt !== "string" && (
                  <p className="article-intro">{post.excerpt}</p>
                )}

                {/* ✅ Full content rendered as paragraphs */}
                <div className="article-body">
                  {post.content
                    .split("\n")
                    .filter((para) => para.trim() !== "")
                    .map((para, index) => (
                      <p key={index} className="article-paragraph">{para}</p>
                    ))}
                </div>

                {/* Post info footer */}
                <div className="article-info-footer">
                  <div className="article-info-row">
                    <span className="article-info-label">Published</span>
                    <span className="article-info-value">{formatDate(post.created_at)}</span>
                  </div>
                  {post.updated_at !== post.created_at && (
                    <div className="article-info-row">
                      <span className="article-info-label">Last Updated</span>
                      <span className="article-info-value">{formatDate(post.updated_at)}</span>
                    </div>
                  )}
                  <div className="article-info-row">
                    <span className="article-info-label">Read Time</span>
                    <span className="article-info-value">{estimateReadTime(post.content)}</span>
                  </div>
                </div>

              </article>

              {/* Sidebar */}
              <aside className="blog-sidebar">

                {/* Share Buttons */}
                <div className="sidebar-share">
                  <h3 className="sidebar-heading">SHARE THIS POST</h3>
                  <div className="share-buttons">
                    <button className="share-btn" onClick={() => handleShare("twitter")} aria-label="Share on Twitter">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12.6 2H14.8L9.7 7.8L15.8 14H11.1L7.4 9.6L3.2 14H1L6.4 7.8L0.5 2H5.3L8.7 6L12.6 2ZM11.7 12.7H13L4.6 3.7H2.9L11.7 12.7Z" fill="#000000" />
                      </svg>
                    </button>
                    <button className="share-btn" onClick={() => handleShare("linkedin")} aria-label="Share on LinkedIn">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M14.5 1H1.5C1.225 1 1 1.225 1 1.5V14.5C1 14.775 1.225 15 1.5 15H14.5C14.775 15 15 14.775 15 14.5V1.5C15 1.225 14.775 1 14.5 1ZM5.5 13H3.5V6H5.5V13ZM4.5 5.125C3.8125 5.125 3.25 4.5625 3.25 3.875C3.25 3.1875 3.8125 2.625 4.5 2.625C5.1875 2.625 5.75 3.1875 5.75 3.875C5.75 4.5625 5.1875 5.125 4.5 5.125ZM13 13H11V9.625C11 8.8125 10.4375 8.25 9.625 8.25C8.8125 8.25 8.25 8.8125 8.25 9.625V13H6.25V6H8.25V6.9375C8.75 6.3125 9.5625 6 10.25 6C11.75 6 13 7.25 13 8.75V13Z" fill="#000000" />
                      </svg>
                    </button>
                    <button className="share-btn" onClick={() => handleShare("copy")} aria-label="Copy link">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M10 6L6.5 9.5M6 10L9.5 6.5M11 5L13 7M8 12L10 14M3 3L5 5M11.5 2.5C12.328 2.5 13 3.172 13 4C13 4.828 12.328 5.5 11.5 5.5C10.672 5.5 10 4.828 10 4C10 3.172 10.672 2.5 11.5 2.5ZM4.5 10.5C5.328 10.5 6 11.172 6 12C6 12.828 5.328 13.5 4.5 13.5C3.672 13.5 3 12.828 3 12C3 11.172 3.672 10.5 4.5 10.5Z" stroke="#000000" strokeWidth="1.33333" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Post Details Card */}
                <div className="sidebar-post-details">
                  <h3 className="sidebar-heading">POST DETAILS</h3>
                  <ul className="sidebar-details-list">
                    <li className="sidebar-details-item">
                      <span className="sidebar-details-label">Published</span>
                      <span className="sidebar-details-value">{formatDate(post.created_at)}</span>
                    </li>
                    <li className="sidebar-details-item">
                      <span className="sidebar-details-label">Read Time</span>
                      <span className="sidebar-details-value">{estimateReadTime(post.content)}</span>
                    </li>
                    <li className="sidebar-details-item">
                      <span className="sidebar-details-label">Status</span>
                      <span className={`sidebar-details-badge ${post.is_published ? "published" : "draft"}`}>
                        {post.is_published ? "Published" : "Draft"}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Back to Blog */}
                <Link to="/blog" className="sidebar-back-btn">
                  ← Back to all posts
                </Link>

              </aside>
            </div>

            {/* Related Articles */}
            <section className="related-articles-section">
              <h2 className="related-articles-heading">More Articles</h2>
              <div className="related-articles-grid">
                {relatedArticles.map((article) => (
                  <Link key={article.id} to={`/blog/${article.id}`} className="related-article-card">
                    <div className="related-article-image-wrapper">
                      <img src={article.image} alt={article.title} className="related-article-image" />
                    </div>
                    <h3 className="related-article-title">{article.title}</h3>
                    <p className="related-article-meta">{article.date} · {article.readTime}</p>
                  </Link>
                ))}
              </div>
            </section>

          </main>
        )}

        {/* ── Footer ── */}
        <footer className="footer-v2" id="contact">
          <div className="container-footer-v2">
            <div className="footer-main-content">
              <div className="footer-brand-section-v2">
                <div className="footer-logo-wrapper">
                  <img src={Logo} alt="Revexia" className="footer-logo-image" />
                </div>
                <p className="footer-brand-description-v2">
                  A platform to turn social media DM engagement into automated revenue with AI-driven personalization.
                </p>
              </div>
              <div className="footer-column-v2">
                <h4 className="footer-column-heading-v2">Informations</h4>
                <ul className="footer-column-links-v2">
                  <li><Link to="/">Home</Link></li>
                  
                </ul>
              </div>
              <div className="footer-column-v2">
                <h4 className="footer-column-heading-v2">Social</h4>
                <ul className="footer-column-links-v2">
                  <li><a href="https://www.facebook.com/share/1AhWEt4FyM/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">Facebook</a></li>
                  <li><a href="https://www.instagram.com/revexia.ai/" target="_blank" rel="noopener noreferrer">Instagram</a></li>
                  <li><a href="https://www.linkedin.com/company/revexiaai/" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
                  <li><a href="https://www.instagram.com/revexia.ai" target="_blank" rel="noopener noreferrer">TikTok</a></li>
                </ul>
              </div>
              <div className="footer-column-v2">
                <h4 className="footer-column-heading-v2">Help</h4>
                <ul className="footer-column-links-v2">
                   <li>
                    <Link to="/contact">Contact</Link>
                  </li>
                  
                  <li>
                    <Link to="/terms">Terms & Conditions</Link>
                  </li>
                  <li>
                    <Link to="/privacy-policy">Privacy Policy</Link>
                  </li>
                  <li>
                    <Link to="/facebook-terms">Facebook Terms</Link>
                  </li>
                </ul>
              </div>
            </div>
            <hr className="footer-divider-line-v2" />
            <div className="footer-bottom-v2">
              <p className="footer-copyright-v2">
                ©2026 Revexia. All rights reserved. Developed by{" "}
                <a href="https://softwarebari.com" target="_blank" rel="noopener noreferrer" className="developer-link">Softwarebari</a>.
              </p>
              <div className="footer-legal-links-v2">
                <Link to="/privacy-policy">Privacy Policy</Link>
                <span className="separator-v2">|</span>
                <Link to="/terms">Terms & Conditions</Link>
                <span className="separator-v2">|</span>
                <Link to="/cookies">Cookies Policy</Link>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
}

export default BlogDetailPage;
